import { z } from 'zod';

import { UnitSystem } from '@optical/utils';

import { playerGroupSchema, playerSchema } from '../../roster';
import { sessionSchema } from '../../sessions';
import { Locale, thresholdsSchema } from '../../settings';
import { ShotLoad } from '../../statistics';
import { CourtArea } from '../court-area';

// ******************* Stats *******************

export type WeeklyReportAreaShotLoadStats = z.output<typeof weeklyReportAreaShotLoadStatsSchema>;
export type WeeklyReportAreaShotLoadStatsDTO = z.input<typeof weeklyReportAreaShotLoadStatsSchema>;

export const weeklyReportAreaShotLoadStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  distr: z.number(),
  xvalue: z.number(),
});

export type WeeklyReportShotLoadStats = z.output<typeof weeklyReportShotLoadStatsSchema>;
export type WeeklyReportShotLoadStatsDTO = z.input<typeof weeklyReportShotLoadStatsSchema>;

export const weeklyReportShotLoadStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  distr: z.number(),
  twopa: z.number(),
  twopm: z.number(),
  twopp: z.number(),
  threepa: z.number(),
  threepm: z.number(),
  threepp: z.number(),
});

export type WeeklyReportAreaShotStats = z.output<typeof weeklyReportAreaShotStatsSchema>;
export type WeeklyReportAreaShotStatsDTO = z.input<typeof weeklyReportAreaShotStatsSchema>;

export const weeklyReportAreaShotStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  distr: z.number(),
  xvalue: z.number(),
});

export type WeeklyReportShotStats = z.output<typeof weeklyReportShotStatsSchema>;
export type WeeklyReportShotStatsDTO = z.input<typeof weeklyReportShotStatsSchema>;

export const weeklyReportShotStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  twopa: z.number(),
  twopm: z.number(),
  twopp: z.number(),
  threepa: z.number(),
  threepm: z.number(),
  threepp: z.number(),
  fta: z.number(),
  ftm: z.number(),
  ftp: z.number(),
  efgp: z.number(),
});

export type WeeklyReportPerformanceStats = z.output<typeof weeklyReportPerformanceStatsSchema>;
export type WeeklyReportPerformanceStatsDTO = z.input<typeof weeklyReportPerformanceStatsSchema>;

export const weeklyReportPerformanceStatsSchema = z.object({
  dist: z.number(),
  distpm: z.number(),
  load: z.number(),
  loadpm: z.number(),
  sprints: z.number(),
  sprintspm: z.number(),
  time: z.number(),
});

export type WeeklyReportStats = z.output<typeof weeklyReportStatsSchema>;
export type WeeklyReportStatsDTO = z.input<typeof weeklyReportStatsSchema>;

export const weeklyReportStatsSchema = z.object({
  // Shot stats
  areaShotLoadStats: z.record(
    z.enum(CourtArea),
    z.record(z.enum(ShotLoad), weeklyReportAreaShotLoadStatsSchema),
  ),
  areaShotStats: z.record(z.enum(CourtArea), weeklyReportAreaShotStatsSchema),

  playerShotLoadStats: z.record(
    z.uuid(),
    z.record(z.enum(ShotLoad), weeklyReportShotLoadStatsSchema),
  ),
  playerShotStats: z.record(z.uuid(), weeklyReportShotStatsSchema),

  shotLoadStats: z.record(z.enum(ShotLoad), weeklyReportShotLoadStatsSchema),
  shotStats: weeklyReportShotStatsSchema,

  // Performance stats (`false` if not available, e.g. for IMU customers)
  performanceStats: z.union([weeklyReportPerformanceStatsSchema, z.literal(false)]),
  playerPerformanceStats: z.union([
    z.record(z.uuid(), weeklyReportPerformanceStatsSchema),
    z.literal(false),
  ]),
});

// ******************* Selection *******************

export type WeeklyReportPlayerSelection = z.output<typeof weeklyReportPlayerSelectionSchema>;
export type WeeklyReportPlayerSelectionDTO = z.input<typeof weeklyReportPlayerSelectionSchema>;

export const weeklyReportPlayerSelectionSchema = z.object({
  wholeTeam: z.boolean(),
  group: playerGroupSchema.nullable(),
  players: z.array(playerSchema),
});

export type WeeklyReportSessionState = z.output<typeof weeklyReportSessionStateSchema>;
export type WeeklyReportSessionStateDTO = z.input<typeof weeklyReportSessionStateSchema>;

export const weeklyReportSessionStateSchema = z.enum(['processed', 'assignable', 'in-progress']);

export type WeeklyReportSessionStates = z.output<typeof weeklyReportSessionStatesSchema>;
export type WeeklyReportSessionStatesDTO = z.input<typeof weeklyReportSessionStatesSchema>;

export const weeklyReportSessionStatesSchema = z.record(z.uuid(), weeklyReportSessionStateSchema);

export type WeeklyReportWeekSelection = z.output<typeof weeklyReportWeekSelectionSchema>;
export type WeeklyReportWeekSelectionDTO = z.input<typeof weeklyReportWeekSelectionSchema>;

export const weeklyReportWeekSelectionSchema = z.object({
  weekId: z.string(),
  sessions: z.array(sessionSchema),
  sessionStates: weeklyReportSessionStatesSchema,
});

export type WeeklyReportSelection = z.output<typeof weeklyReportSelectionSchema>;
export type WeeklyReportSelectionDTO = z.input<typeof weeklyReportSelectionSchema>;

export const weeklyReportSelectionSchema = z.object({
  playerSelection: weeklyReportPlayerSelectionSchema,
  weekSelection: weeklyReportWeekSelectionSchema,
});

// ******************* Settings *******************

export type WeeklyReportSettings = z.output<typeof weeklyReportSettingsSchema>;
export type WeeklyReportSettingsDTO = z.input<typeof weeklyReportSettingsSchema>;

export const weeklyReportSettingsSchema = z.object({
  locale: z.enum(Locale),
  thresholds: thresholdsSchema,
  unitSystem: z.enum(UnitSystem),
});

// ******************* Data *******************

export type WeeklyReportData = z.output<typeof weeklyReportDataSchema>;
export type WeeklyReportDataDTO = z.input<typeof weeklyReportDataSchema>;

export const weeklyReportDataSchema = z.object({
  // Report data
  stats: weeklyReportStatsSchema,

  // Metadata
  selection: weeklyReportSelectionSchema,
  settings: weeklyReportSettingsSchema,
});
